package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.translateX
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.icons.CalendarIcon
import com.varabyte.kobweb.silk.components.icons.ChevronLeftIcon
import com.varabyte.kobweb.silk.components.icons.ChevronRightIcon
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button as HtmlButton
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent

/*
 * Range date picker ("airport ticket" interaction): click the first day, click the last, then
 * confirm — bounded by `DateRange.MAX_DAYS`. The rules live in `DateRange`/`RangeSelection` and are
 * unit-tested; this file is only the calendar grid and the overlay plumbing.
 *
 * ESC cancels (nothing is applied), Enter confirms, and the picked interval is only handed to the
 * caller on confirmation (an accidental second click must not silently change the form).
 *
 * Silk ships no date picker, so the grid is raw `<button>`s (keyboard + ARIA for free) inside the
 * same `Overlay` pattern used by the removal dialog.
 */

val DatePickerCardStyle = CssStyle.base {
    Modifier
        .backgroundColor(IndustrialPalette.White)
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.375.cssRem)
        .padding(1.25.cssRem)
        .width(21.cssRem)
}

val DatePickerMonthStyle = CssStyle.base {
    Modifier.fontWeight(FontWeight.SemiBold).fontSize(0.875.cssRem).textAlign(TextAlign.Center)
}

val DatePickerGridStyle = CssStyle.base {
    Modifier
        // Without an explicit width the grid shrinks to 7 × the cell min-width and sits left.
        .width(100.percent)
        .display(DisplayStyle.Grid)
        .styleModifier {
            property("grid-template-columns", "repeat(7, 1fr)")
            property("justify-items", "center")
        }
        .gap(0.125.cssRem)
        .margin(top = 0.5.cssRem)
}

val DatePickerWeekdayStyle = CssStyle.base {
    Modifier
        .fontSize(0.6875.cssRem)
        .color(IndustrialPalette.Muted)
        .textAlign(TextAlign.Center)
        .padding(bottom = 0.25.cssRem)
}

/** Borderless square button with a real hit area for the month arrows. */
val DatePickerNavButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
            .setVariable(ButtonVars.BackgroundHoverColor, IndustrialPalette.Surface)
            .setVariable(ButtonVars.BackgroundPressedColor, IndustrialPalette.Surface)
            .setVariable(ButtonVars.Color, IndustrialPalette.Steel)
            .fontSize(1.cssRem)
            .minWidth(2.cssRem)
            .height(2.cssRem)
            .padding(0.cssRem)
            .border(0.px, LineStyle.Solid, Colors.Transparent)
            .borderRadius(0.25.cssRem)
    }
}

sealed interface DayKind : com.varabyte.kobweb.silk.style.ComponentKind

val DayStyle = CssStyle<DayKind> {
    base {
        Modifier
            .minWidth(2.cssRem)
            .minHeight(1.875.cssRem)
            .padding(0.25.cssRem, 0.cssRem)
            .fontSize(0.8125.cssRem)
            .backgroundColor(Colors.Transparent)
            .color(IndustrialPalette.Ink)
            .border(0.px, LineStyle.Solid, Colors.Transparent)
            .borderRadius(0.1875.cssRem)
            .cursor(Cursor.Pointer)
            .textAlign(TextAlign.Center)
            // Raw <button>: strip the native chrome so it reads as a calendar cell.
            .styleModifier {
                property("font-family", "inherit")
                property("appearance", "none")
            }
    }
}

/** Days inside the selected range. */
val DaySelectedVariant = DayStyle.addVariantBase {
    Modifier.backgroundColor(IndustrialPalette.SteelTint).color(IndustrialPalette.Steel)
}

/** The two range endpoints. */
val DayEdgeVariant = DayStyle.addVariantBase {
    Modifier.backgroundColor(IndustrialPalette.Steel).color(IndustrialPalette.White)
}

val DayDisabledVariant = DayStyle.addVariantBase {
    Modifier.color(IndustrialPalette.Border).cursor(Cursor.Default)
}

@Composable
internal fun DateRangePicker(
    range: DateRange,
    onRangeChange: (DateRange) -> Unit,
    modifier: Modifier = Modifier,
    id: String = "stay-date-range",
    minDate: CivilDate = CivilDate.today(),
) {
    var open by remember { mutableStateOf(false) }
    var selection by remember { mutableStateOf(RangeSelection(range)) }
    var cursor by remember { mutableStateOf(firstOfMonth(range.start ?: minDate)) }

    /** Shown in red in place of the hint when a click would exceed the maximum span. */
    var error by remember { mutableStateOf("") }

    /** Incremented on a refused click; each tick replays the shake animation. */
    var shakeTick by remember { mutableStateOf(0) }
    var shakeOffset by remember { mutableStateOf(0) }

    LaunchedEffect(shakeTick) {
        if (shakeTick == 0) return@LaunchedEffect
        for (offset in listOf(-6, 6, -5, 5, -3, 3, 0)) {
            shakeOffset = offset
            delay(45)
        }
    }

    val label = when {
        range.isComplete -> "${range.start!!.br} – ${range.end!!.br}"
        range.start != null -> "${range.start!!.br} – escolha o último dia"
        else -> "Escolher datas"
    }

    fun confirm() {
        val chosen = selection.range
        if (!chosen.isComplete) return
        error = ""
        onRangeChange(chosen)
        open = false
    }

    Button(
        onClick = {
            selection = RangeSelection(range)
            cursor = firstOfMonth(range.start ?: minDate)
            error = ""
            open = true
        },
        variant = DefaultButtonVariant,
        size = ButtonSize.SM,
        modifier = modifier.then(Modifier.id(id)),
    ) {
        CalendarIcon(Modifier.fontSize(0.8125.cssRem).margin(right = 0.375.cssRem))
        Text(label)
    }

    if (!open) return

    // Re-registered whenever the selection changes so Enter always confirms what is on screen.
    DisposableEffect(selection) {
        val listener = EventListener { event ->
            when ((event as? KeyboardEvent)?.key) {
                "Escape" -> open = false
                "Enter" -> confirm()
                else -> Unit
            }
        }
        window.addEventListener("keydown", listener)
        onDispose { window.removeEventListener("keydown", listener) }
    }

    Overlay(contentAlignment = Alignment.Center) {
        Box(Modifier.fillMaxSize().onClick { open = false })

        Column(
            modifier = DatePickerCardStyle.toModifier()
                .then(Modifier.translateX(shakeOffset.px))
                .then(Modifier.id("$id-dialog")),
        ) {
            Row(
                modifier = Modifier.width(100.percent),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Button(
                    onClick = { cursor = cursor.plusDays(-1).let(::firstOfMonth) },
                    variant = DatePickerNavButtonVariant,
                    size = ButtonSize.SM,
                ) {
                    ChevronLeftIcon(Modifier.fontSize(1.cssRem))
                }
                Div(attrs = DatePickerMonthStyle.toModifier().width(100.percent).toAttrs()) {
                    Text("${CivilDate.MONTH_NAMES[cursor.month - 1]} ${cursor.year}")
                }
                Button(
                    onClick = { cursor = cursor.plusDays(32).let(::firstOfMonth) },
                    variant = DatePickerNavButtonVariant,
                    size = ButtonSize.SM,
                ) {
                    ChevronRightIcon(Modifier.fontSize(1.cssRem))
                }
            }

            Div(attrs = DatePickerGridStyle.toAttrs()) {
                for (weekday in CivilDate.WEEKDAY_LABELS) {
                    Div(attrs = DatePickerWeekdayStyle.toAttrs()) { Text(weekday) }
                }
                // Monday-first grid: pad the first week with empty cells (columnIndex is 0-based).
                repeat(cursor.columnIndex) {
                    Div(attrs = Modifier.toAttrs()) { }
                }
                for (day in 1..CivilDate.lengthOfMonth(cursor.year, cursor.month)) {
                    val date = cursor.withDay(day)
                    val disabled = date < minDate
                    val selected = selection.range.contains(date) && selection.range.start != null
                    val edge = date == selection.range.start || date == selection.range.end
                    HtmlButton(
                        attrs = DayStyle
                            .toModifier(
                                when {
                                    disabled -> DayDisabledVariant
                                    edge -> DayEdgeVariant
                                    selected -> DaySelectedVariant
                                    else -> null
                                }
                            )
                            .toAttrs {
                                attr("type", "button")
                                attr("aria-label", date.br)
                                attr("aria-pressed", selected.toString())
                                if (disabled) attr("disabled", "")
                                onClick {
                                    if (disabled) return@onClick
                                    val refusal = selection.clampError(date)
                                    if (refusal != null) {
                                        // Refuse the click and keep the anchor: restarting the
                                        // range here looked like a bug, not like a limit.
                                        error = refusal
                                        shakeTick++
                                        return@onClick
                                    }
                                    error = ""
                                    selection = selection.click(date)
                                }
                            },
                    ) {
                        Text(day.toString())
                    }
                }
            }

            P(
                attrs = Modifier
                    .fontSize(0.6875.cssRem)
                    .color(if (error.isNotEmpty()) IndustrialPalette.Red else IndustrialPalette.Muted)
                    .margin(top = 0.5.cssRem)
                    .toAttrs {
                        if (error.isNotEmpty()) attr("role", "alert")
                    },
            ) {
                Text(
                    if (error.isNotEmpty()) {
                        error
                    } else {
                        when {
                            selection.range.isComplete ->
                                "${selection.range.start!!.br} – ${selection.range.end!!.br} (${selection.range.size} dia(s))"
                            selection.range.start != null -> "Escolha o último dia (máximo de ${selection.maxDays} dias)."
                            else -> "Selecione o primeiro e o último dia (máximo de ${selection.maxDays} dias)."
                        }
                    }
                )
            }

            Row(
                modifier = Modifier.width(100.percent).gap(0.375.cssRem).margin(top = 0.75.cssRem),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Button(
                    onClick = {
                        selection = selection.clear()
                        error = ""
                        onRangeChange(DateRange())
                        open = false
                    },
                    variant = LinkButtonVariant,
                    size = ButtonSize.SM,
                ) {
                    Text("Limpar")
                }
                Spacer()
                Button(onClick = { open = false }, variant = DefaultButtonVariant, size = ButtonSize.SM) {
                    Text("Cancelar")
                }
                Button(
                    onClick = { confirm() },
                    variant = PrimaryButtonVariant,
                    size = ButtonSize.SM,
                    enabled = selection.range.isComplete,
                ) {
                    Text("Confirmar")
                }
            }
        }
    }
}

private fun firstOfMonth(date: CivilDate): CivilDate = CivilDate(date.year, date.month, 1)
